import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Card, Row, Col, Button, Badge, ListGroup, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
// Using project Utility for toast notifications
import { getVariantOptionDetails, deleteVariantOption } from '../../Controller/VariantOptionController';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';

// import '../../../Assets/css/variant-options.scss';

/**
 * Component to view variant option details
 * @returns {JSX.Element} VariantOptionView component
 */
const VariantOptionView = (props) => {
  const { slack, lng } = useParams();

  // State for variant option details
  const [variantOption, setVariantOption] = useState({});
  const [loading, setLoading] = useState(true);

  // State for delete confirmation
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  // Fetch variant option details on component mount
  useEffect(() => {
    fetchVariantOptionDetails();
  }, [slack]);

  /**
   * Fetch variant option details
   */
  const fetchVariantOptionDetails = async () => {
    setLoading(true);
    try {
      const response = await getVariantOptionDetails(slack);

      if (response.success && response.data) {
        setVariantOption(response.data);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch variant option details', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching variant option details:', error);
      Utility.toastNotifications('An error occurred while fetching variant option details', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Confirm delete operation
   */
  const confirmDelete = () => {
    setShowDeleteAlert(true);
  };

  /**
   * Cancel delete operation
   */
  const cancelDelete = () => {
    setShowDeleteAlert(false);
  };

  /**
   * Handle delete operation after confirmation
   */
  const handleDelete = async () => {
    try {
      const response = await deleteVariantOption(slack);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Variant option deleted successfully', 'Success', 'success');
        history.push(`/${lng}/settings/variant-options`);
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete variant option', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting variant option:', error);
      Utility.toastNotifications('An error occurred while deleting variant option', 'Error', 'error');
    } finally {
      setShowDeleteAlert(false);
    }
  };

  const history = useHistory();

  //======= add action permission ====//
  // Check multiple permissions at once
  const variantOptionViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'variant_options',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  return (
    <div className="content">
      {/* Toast notifications handled by Utility */}
      <div className="page-header">
        <div className="page-title">
          <h4>
            <Link to={`/${lng}/settings/variant-options`}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            </Link>
            Variant Option Details
          </h4>
        </div>
        <div className="page-btn">
          {variantOptionViewPermissions.edit && (
            <Link to={`/${lng}/settings/variant-options/edit/${slack}`} className="btn btn-primary me-2">
              <FontAwesomeIcon icon={faEdit} className="me-1" /> Edit
            </Link>
          )}
          {variantOptionViewPermissions.delete && (
            <Button variant="danger" onClick={confirmDelete}>
              <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading variant option details...</p>
            </div>
          ) : (
            <Row>
              <Col md={6}>
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">Basic Information</h5>
                  </Card.Header>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Variant Option Code:</Col>
                        <Col md={8}>{variantOption.variantOptionCode || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Label:</Col>
                        <Col md={8}>{variantOption.label || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Status:</Col>
                        <Col md={8}>
                          <Badge bg={variantOption.status === 1 ? 'success' : 'danger'} className={variantOption.status_data?.color || ''}>
                            {variantOption.status_data?.label || (variantOption.status === 1 ? 'Active' : 'Inactive')}
                          </Badge>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h5 className="mb-0">System Information</h5>
                  </Card.Header>
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">ID:</Col>
                        <Col md={8}>{variantOption.id || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Created On:</Col>
                        <Col md={8}>{Utility.get12HourFormat(variantOption.createdAt) || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Last Updated:</Col>
                        <Col md={8}>{Utility.get12HourFormat(variantOption.updatedAt) || 'N/A'}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Created By:</Col>
                        <Col md={8}>{(variantOption.CreatedUser ? `${variantOption.CreatedUser.first_name} ${variantOption.CreatedUser.last_name}` : 'N/A')}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col md={4} className="fw-bold">Updated By:</Col>
                        <Col md={8}>{(variantOption.UpdatedUser ? `${variantOption.UpdatedUser.first_name} ${variantOption.UpdatedUser.last_name}` : 'N/A')}</Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </Card>
              </Col>
            </Row>
          )}

          {/* Delete Confirmation Alert */}
          {showDeleteAlert && (
            <Modal show={showDeleteAlert} onHide={cancelDelete} centered>
              <Modal.Header closeButton>
                <Modal.Title>Confirmation</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to delete this"
                  BodySecondContent={variantOption.label}
                  BodyThirdContent="variant option?"
                  confirmationButtonContent="Yes, delete it!"
                  cancelButtonContent="Cancel"
                  deleteIconVissable={true}
                  deleteConfirmButton={handleDelete}
                  deleteCancleButton={cancelDelete}
                />
              </Modal.Body>
            </Modal>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token
  };
}

export default connect(mapStateToProps)(withNamespaces()(VariantOptionView));

